// Constants
import { ClientFlavor, PlanType } from "@ms/uno-constants/lib/local/AppConstants";
import { EngagementTelemetryConstants } from "@ms/uno-constants/lib/local/EngagementTelemetryConstants";
import { ModalType } from "@ms/uno-constants/lib/local/ModalType";
import { SubRouteType } from "@ms/uno-constants/lib/local/RoutingConstants";
import { STORE_ENTITY_STATUS_ABSENT, StoreEntityState } from "@ms/uno-constants/lib/local/StoreConstants";
// Controls
import { ContentSensitivityLabelContainer } from "@ms/planner-container-controls/lib/local/containers/contentSensitivityLabel/ContentSensitivityLabelContainer";
import { CopyBundled, DeleteBundled, ExcelLogoBundled, InfoBundled } from "@ms/planner-generic-controls/lib/local/icons";
import { PlanHeaderInfo } from "@ms/planner-presentational-controls/lib/local/planHeader/planHeaderInfo/PlanHeaderInfo";
import { PlanSettingsPanelContainer } from "../planSettings/PlanSettingsPanelContainer";
// Hooks
import { useActionCreators } from "@ms/planner-container-controls/lib/local/hooks/useActionCreators";
import { useConfigProvider } from "@ms/planner-container-controls/lib/local/hooks/useConfigProvider";
import { useStores } from "@ms/planner-container-controls/lib/local/hooks/useStores";
import { useLoggers } from "@ms/planner-presentational-controls/lib/local/utilities/hooks/useLoggers";
// Stores
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Action Creators
import { ActionCreator } from "@ms/planner-actioncreators/lib/local/actioncreators/ActionCreator";
// Models
import { ExportOperationFormat, ExportOperationSource } from "@ms/planner-models/lib/local/client/export/Enumerations";
import { ContainerType } from "@ms/planner-models/lib/local/service/planner/enumerations/IPlannerEnumerations";
// Telemetry
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Utilities
import { guardPinLimit, showPinLimitReachedModal } from "@ms/planner-actioncreators/lib/local/utilities/PinPlanUtilities";
import { getConnectedPlanIdFromPLinkUrl, getPLinkRoute, getPlanParentTaskPLinks, getTaskIdFromPlanParentTaskPLink } from "@ms/planner-models/lib/local/utilities/PlanLinkUtilities";
import { getContainerName } from "@ms/planner-stores/lib/local/utilities/ContainerUtilities";
import { getPlanPermissionsFromStores } from "@ms/planner-stores/lib/local/utilities/PermissionsUtilities";
import { Strings } from "@ms/uno-resources/lib/local/CultureUtilities";
import { safelyOpenExternalUrl } from "@ms/uno-utilities/lib/local/BrowserUtilities";
import * as React from "react";
const componentName = "PlanHeaderInfoContainer";
/**
 * Smart component for plan header info section
 */ export const PlanHeaderInfoContainer = ({ planId, planSettingsPanelController })=>{
    const configProvider = useConfigProvider();
    const { traceLogger, engagementLogger } = useLoggers();
    const [modernGroupsStore, mruStore, planStore, permissionsStore, intelligenceStore, rosterStore, userStore, appContextStore, teamsChannelStore, driveItemStore, todoStore, customFieldDefinitionStore, dependencyStore, taskStore] = useStores([
        Store.ModernGroups,
        Store.Mru,
        Store.Plan,
        Store.Permissions,
        Store.Intelligence,
        Store.Roster,
        Store.User,
        Store.AppContext,
        Store.TeamsChannel,
        Store.DriveItem,
        Store.Todo,
        Store.CustomFieldDefinition,
        Store.Dependency,
        Store.Tasks
    ]);
    const [routingActionCreator, mruActionCreator, modalActionCreator, exportActionCreator, intelligenceActionCreator, planActionCreator, containerActionCreator] = useActionCreators([
        ActionCreator.Routing,
        ActionCreator.Mru,
        ActionCreator.Modal,
        ActionCreator.Export,
        ActionCreator.Intelligence,
        ActionCreator.Plan,
        ActionCreator.Container
    ]);
    // Get image suggestions directly from store
    const imageSuggestions = intelligenceStore.getImageSuggestions(planId).data?.results ?? null;
    const currentPlan = planStore.getPlan(planId).data ?? null;
    if (!currentPlan) {
        return null;
    }
    const connectedPLinks = configProvider().flights.EnableEvoPLink ? getPlanParentTaskPLinks(currentPlan.links) : [];
    const connectedPlansById = new Map();
    connectedPLinks.forEach((contentLink)=>{
        const connectedPlanId = getConnectedPlanIdFromPLinkUrl(contentLink.webUrl);
        const connectedTaskId = getTaskIdFromPlanParentTaskPLink(contentLink);
        const connectedPlanResult = connectedPlanId ? planStore.getPlan(connectedPlanId) : null;
        const connectedPlan = connectedPlanResult?.data ?? null;
        const connectedPlanStatus = connectedPlanResult?.status ?? STORE_ENTITY_STATUS_ABSENT;
        if (connectedPlanStatus.state === StoreEntityState.Absent || connectedPlanStatus.state === StoreEntityState.NotFound || connectedPlanStatus.isFetching) {
            return;
        }
        const isConnectedPlanAccessible = connectedPlanStatus.state === StoreEntityState.Present && !connectedPlanStatus.isFetching;
        const connectedTask = connectedTaskId ? taskStore.getTask(connectedTaskId).data : null;
        const connectedPlanKey = connectedPlanId ?? contentLink.webUrl;
        const connectedTaskData = {
            contentLink,
            displayName: connectedTask?.displayName || contentLink.displayName
        };
        const existingConnectedPlan = connectedPlansById.get(connectedPlanKey);
        if (existingConnectedPlan) {
            existingConnectedPlan.tasks.push(connectedTaskData);
        } else {
            connectedPlansById.set(connectedPlanKey, {
                id: connectedPlanKey,
                displayName: isConnectedPlanAccessible && connectedPlan?.displayName ? connectedPlan.displayName : Strings.EntityTypeLabel_Plan,
                status: connectedPlanStatus,
                tasks: [
                    connectedTaskData
                ]
            });
        }
    });
    const connectedPlans = Array.from(connectedPlansById.values());
    const planContainer = currentPlan.container;
    const planContainerId = planContainer.containerId;
    const planContainerType = planContainer.containerType ?? null;
    const containerPhoto = planContainerType === ContainerType.Group && planContainerId != null ? modernGroupsStore.getModernGroupPhotoById(planContainerId) ?? null : null;
    const containerDisplayName = getContainerName(currentPlan.container, {
        modernGroupsStore,
        rosterStore,
        userStore,
        teamsChannelStore,
        appContextStore,
        planStore,
        driveItemStore,
        todoStore
    });
    const isTabView = configProvider().sessionMetaData.appMetadata.clientFlavor === ClientFlavor.Tab;
    /**
     * Toggle pin/unpin status of the plan
     */ const togglePlanPinUnpinStatus = ()=>{
        const mruItem = mruStore.getMruItemByServerDocId(planId) ?? null;
        if (mruItem == null) {
            traceLogger.logTrace(0x1e1583c3 /* tag_4fypd */ , TraceLevel.Warning, `PlanHeaderInfoContainer pin/unpin clicked but mru item was not present in the store [ServerDocId=${planId}]`);
            return;
        }
        const isPinned = mruItem?.isPinned ?? false;
        if (!guardPinLimit(mruStore, isPinned)) {
            showPinLimitReachedModal(modalActionCreator);
            return;
        }
        engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.PlanPinned, {
            isPinned: String(!isPinned)
        });
        const updatedMruItem = mruItem.setProperty("isPinned", !isPinned);
        mruActionCreator.upsertMruItem(updatedMruItem);
    };
    const openPlanSettingsPanel = (ev)=>{
        const elementToFocusOnDismiss = ev && "currentTarget" in ev ? ev.currentTarget : ev;
        planSettingsPanelController.open(elementToFocusOnDismiss);
    };
    const isPlanFavorite = mruStore.getMruItemByServerDocId(planId)?.isPinned ?? false;
    const planPermissions = getPlanPermissionsFromStores(currentPlan, {
        permissionsStore,
        modernGroupsStore,
        rosterStore,
        userStore,
        teamsChannelStore,
        appContextStore,
        planStore,
        driveItemStore,
        todoStore,
        dependencyStore,
        taskStore
    }, traceLogger);
    /**
     * Open plan settings panel with engagement logging
     */ const onClickPlanDetails = (ev)=>{
        engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.PlanSettingsClicked, {
            planId
        });
        openPlanSettingsPanel(ev);
    };
    /**
     * Open copy plan modal with engagement logging
     */ const onClickCopyPlan = ()=>{
        const extraData = {
            planId
        };
        // hasCustomFields is only meaningful when the custom fields flight is on.
        if (configProvider().flights.EnableCustomFields) {
            const customFieldDefinitionsResult = customFieldDefinitionStore.getCustomFieldDefinitionsForPlan(planId);
            if (customFieldDefinitionsResult.status.state === StoreEntityState.Present) {
                extraData.hasCustomFields = String(customFieldDefinitionsResult.data.length > 0);
            }
        }
        engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.CopyPlanButtonClicked, extraData);
        modalActionCreator.openModal({
            modalType: ModalType.CopyPlan,
            planId
        });
    };
    /**
     * Export plan to Excel with engagement logging
     */ const onClickExportPlan = ()=>{
        engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.ExportPlanButtonClicked, {
            planId
        });
        exportActionCreator.requestExportOperation(planId, ExportOperationFormat.Excel, ExportOperationSource.Plan);
    };
    /**
     * Navigate to My Plans with engagement logging
     */ const onClickMyPlansBreadcrumb = ()=>{
        engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.MyPlansBreadcrumbClicked);
        routingActionCreator.navigate({
            subRouteType: SubRouteType.MyPlans,
            subRoute: []
        }, undefined, {
            startTime: performance.now(),
            entryPoint: "planBreadcrumb"
        });
    };
    /**
     * Open plan settings from title breadcrumb with engagement logging
     */ const onClickPlanTitleBreadcrumb = (ev)=>{
        engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.PlanTitleBreadcrumbClicked, {
            planId
        });
        openPlanSettingsPanel(ev);
    };
    /** Navigate to a connected task in the current window with engagement logging. */ const onClickConnectedTask = (contentLink)=>{
        engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.ConnectedPlanMenuItemClicked, {
            planId
        });
        const pLinkRoute = getPLinkRoute(contentLink.webUrl);
        const connectedPlan = pLinkRoute ? planStore.getPlan(pLinkRoute.planId).data : null;
        const taskId = pLinkRoute?.taskId ?? null;
        if (!connectedPlan || !taskId) {
            safelyOpenExternalUrl(contentLink.webUrl, traceLogger, "_self");
            return;
        }
        const planType = connectedPlan.getPlanType();
        switch(planType){
            case PlanType.Basic:
            case PlanType.Unified:
                routingActionCreator.navigateToPlan({
                    planType,
                    planId: connectedPlan.id,
                    planName: connectedPlan.displayName,
                    taskId
                });
                return;
            default:
                traceLogger.logTrace(0x1d79c31a /* tag_342m0 */ , TraceLevel.Warning, `PlanHeaderInfoContainer onClickConnectedTask: plan type ${planType} is not supported]`);
                break;
        }
    };
    const onOpenConnectedPlansMenu = ()=>{
        engagementLogger.logEngagement(componentName, EngagementTelemetryConstants.PLinkConnectedPlansMenuOpened, {
            planId
        });
    };
    const staticMenuItems = [];
    // Only show plan settings for non-ToDo containers
    planContainer.containerType !== ContainerType.ToDo && staticMenuItems.push({
        key: "planDetails",
        text: Strings.PlanHeader_PlanHeaderPlanSettings,
        icon: /*#__PURE__*/ React.createElement(InfoBundled, null),
        onClick: onClickPlanDetails,
        dataTestId: "plan-header-plan-details-menu-item"
    });
    const enableContentLevelSensitivityLabels = configProvider().flights.EnableContentLevelSensitivityLabelsEvo;
    const sensitivityPermissions = permissionsStore.getPlanPermissionsFromUserRightsObject(planId);
    const canExportDisallowedBySensitivity = enableContentLevelSensitivityLabels && !planPermissions.canExport && !sensitivityPermissions.canExportAllowedBySensitivity;
    const onDeletePlan = ()=>{
        modalActionCreator.openModal({
            modalType: ModalType.DeletePlan,
            planId
        });
    };
    planPermissions.canCopy && staticMenuItems.push({
        key: "copyPlan",
        text: Strings.PlanHeader_DuplicatePlanText,
        icon: /*#__PURE__*/ React.createElement(CopyBundled, null),
        onClick: onClickCopyPlan,
        dataTestId: "plan-header-duplicate-plan-menu-item"
    });
    planContainer.containerType !== ContainerType.ToDo && staticMenuItems.push({
        key: "exportPlan",
        text: Strings.PlanHeader_ExportAsExcelText,
        icon: /*#__PURE__*/ React.createElement(ExcelLogoBundled, null),
        onClick: onClickExportPlan,
        disabled: canExportDisallowedBySensitivity,
        title: canExportDisallowedBySensitivity ? Strings.PlanHeader_ExportDisabledBySensitivityTooltip : undefined,
        dataTestId: "plan-header-export-excel-menu-item"
    });
    !isTabView && staticMenuItems.push({
        key: "deletePlan",
        text: Strings.PlanSettings_DeletePlan,
        icon: /*#__PURE__*/ React.createElement(DeleteBundled, null),
        onClick: onDeletePlan,
        disabled: !planPermissions.canDelete,
        title: !planPermissions.canDelete ? Strings.PlanSettings_CannotDeletePlan : undefined,
        dataTestId: "plan-header-delete-plan-menu-item"
    });
    /**
     * Fetch image suggestions for a given title.
     */ const fetchImageSuggestionsForTitle = (id, title)=>{
        intelligenceActionCreator.fetchImageRecommendations(id, title);
    };
    const onUpdatePlanBackground = (planId, imageUrl)=>{
        const updatedVisuals = {
            ...currentPlan.planView.visuals,
            background: {
                ...currentPlan.planView.visuals.background,
                overlayImage: imageUrl
            }
        };
        const updatedPlanView = {
            ...currentPlan.planView,
            visuals: updatedVisuals
        };
        const updatedPlan = currentPlan.setProperty("planView", updatedPlanView);
        planActionCreator.updatePlan(currentPlan, updatedPlan);
    };
    const onUpdatePlanTitle = (planTitle)=>{
        const updatedPlan = currentPlan.setProperty("displayName", planTitle);
        planActionCreator.updatePlan(currentPlan, updatedPlan);
        const mruItem = mruStore.getMruItemByServerDocId(planId) ?? null;
        if (mruItem != null) {
            const updatedMruItem = mruItem.setProperty("title", planTitle);
            mruActionCreator.upsertMruItem(updatedMruItem);
        } else {
            traceLogger.logTrace(0x1e11b653 /* tag_4e1zt */ , TraceLevel.Warning, `PlanHeaderInfoContainer plan title updated but MRU item was not present in the store [ServerDocId=${planId}]`);
        }
    };
    const renderPlanPanel = ()=>{
        return /*#__PURE__*/ React.createElement(PlanSettingsPanelContainer, {
            planId: planId,
            isOpen: planSettingsPanelController.isOpen,
            onDismiss: planSettingsPanelController.dismiss,
            onUpdatePlanTitle: onUpdatePlanTitle,
            onDeletePlan: onDeletePlan,
            elementToFocusOnDismiss: planSettingsPanelController.elementToFocusOnDismiss,
            initialTab: planSettingsPanelController.initialTab,
            backgroundSuggestions: planPermissions.canUpdateVisuals ? imageSuggestions : [],
            onUpdatePlanBackground: onUpdatePlanBackground,
            fetchImageSuggestions: fetchImageSuggestionsForTitle
        });
    };
    return /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(PlanHeaderInfo, {
        myPlansBreadcrumbItem: {
            key: "MyPlansBreadcrumb",
            text: Strings.Views_MyPlansTitleEvo,
            onClick: onClickMyPlansBreadcrumb
        },
        planBreadcrumbItem: {
            key: "PlanTitleBreadcrumb",
            text: currentPlan.displayName,
            onClick: planContainer.containerType !== ContainerType.ToDo ? onClickPlanTitleBreadcrumb : undefined
        },
        plan: currentPlan,
        isCurrentPlanPinned: isPlanFavorite,
        togglePlanPinUnpinStatus: togglePlanPinUnpinStatus,
        isTabView: isTabView,
        isReadOnly: false,
        onRenderPlanPhoto: ()=>containerActionCreator.fetchContainerPhoto(planContainer),
        renderContentSensitivityLabelContainer: ()=>/*#__PURE__*/ React.createElement(ContentSensitivityLabelContainer, {
                planId: planId
            }),
        imageSource: containerPhoto,
        staticMenuItems: staticMenuItems,
        containerDisplayName: containerDisplayName,
        isConnectedPlansEnabled: configProvider().flights.EnableEvoPLink,
        connectedPlans: connectedPlans,
        onClickConnectedTask: onClickConnectedTask,
        onOpenConnectedPlansMenu: onOpenConnectedPlansMenu
    }), renderPlanPanel());
};
